/**
 * Paquetes de marca compilados en el front.
 *
 * Una edición (un cliente, una instancia desplegada) elige con `branding.marca`
 * con qué marca se vende: Consiglia o Match360. El paquete aporta todo lo que la
 * marca necesita para verse completa —logos SVG en sus cuatro variantes, símbolo,
 * favicon, icono de aplicación, tipografía, color de navegador y los textos de
 * acceso— y la edición sólo pisa lo que quiera personalizar. La paleta de cada
 * marca vive en `src/styles.css`, bajo `[data-brand=…]`, porque cambia entre el
 * tema claro y el oscuro y un estilo en línea no puede hacerlo.
 *
 * Los SVG salen tal cual de los paquetes `*-brand-assets` (v1.0 Consiglia, v1.1
 * Match360) y viven en `public/brands/<marca>/`. Los manuales prohíben redibujar el
 * logotipo: aquí sólo se referencian.
 *
 * OJO: `src/index.html` repite, en su script de arranque, la deducción de la marca
 * y las rutas de favicon, fuente y logo de cada paquete para pintarlas antes de
 * que cargue Angular. Un cambio de ruta aquí va también allí.
 */

export type MarcaId = 'consiglia' | 'match360';

export const MARCAS: readonly MarcaId[] = ['consiglia', 'match360'];

/** Las cuatro variantes de color que trae cada pieza del paquete. */
export interface VariantesLogo {
  /** Azul + segundo color, sobre claro. Uso primario. */
  color: string;
  /** Blanco + segundo color claro, sobre azul de marca u oscuro. */
  negativo: string;
  /** Una tinta azul sobre claro: sellos, documentos. */
  monoAzul: string;
  /** Blanco sobre fotografía o color donde el segundo color no contrasta. */
  monoBlanco: string;
}

export interface PaqueteMarca {
  id: MarcaId;
  /** Nombre de la marca tal como lo escribe su manual. */
  nombre: string;
  lema: string;
  /** Familia de interfaz, ya declarada en `src/styles.css`. */
  fuente: string;
  /** Archivo de la fuente de interfaz, para precargarla (ruta estable en `public/`). */
  fuenteArchivo: string;
  /** Lockup horizontal sin lema: barras de navegación, espacios bajos. */
  horizontal: VariantesLogo;
  /** Lockup horizontal con lema: portadas, acceso, reportes. */
  horizontalLema: VariantesLogo;
  /** Lockup vertical: formatos cuadrados. */
  vertical: VariantesLogo;
  /** Sólo el símbolo: favicon, avatar, espacios bajo el mínimo. */
  simbolo: VariantesLogo;
  /** Favicon SVG que se aclara solo en modo oscuro del sistema. */
  favicon: string;
  /** Icono de aplicación (símbolo en negativo a sangre). */
  appIcon: string;
  /** Color de la barra del navegador móvil. */
  colorNavegador: string;
  /**
   * Colores de identidad en hex, para lo que no puede leer variables CSS: el
   * informe PDF y la vista previa de la pantalla de marca. La paleta de interfaz
   * está en `src/styles.css`.
   */
  identidad: {
    azul: string;
    segundo: string;
    segundoClaro: string;
    /** Color de acento de la interfaz (botón primario, estado activo). */
    interfaz: string;
  };
  /**
   * Textos propios de la marca. Se ponen por encima del literal compilado y por
   * debajo de lo que sirva el backend, así que siguen siendo editables desde
   * Sistema › Textos.
   */
  textos: Readonly<Record<string, string>>;
}

function variantes(marca: MarcaId, pieza: string): VariantesLogo {
  const base = `/brands/${marca}/${pieza.startsWith('simbolo') ? 'simbolo' : 'logo'}/${marca}-${pieza}`;
  return {
    color: `${base}-color.svg`,
    negativo: `${base}-negativo.svg`,
    monoAzul: `${base}-mono-azul.svg`,
    monoBlanco: `${base}-mono-blanco.svg`,
  };
}

export const PAQUETES_MARCA: Readonly<Record<MarcaId, PaqueteMarca>> = {
  consiglia: {
    id: 'consiglia',
    nombre: 'Consiglia',
    lema: 'Conciliación y Control al Instante',
    fuente: 'Rethink Sans Variable',
    fuenteArchivo: '/fonts/rethink-sans-latin-wght-normal.woff2',
    horizontal: variantes('consiglia', 'horizontal'),
    horizontalLema: variantes('consiglia', 'horizontal-lema'),
    vertical: variantes('consiglia', 'vertical'),
    simbolo: variantes('consiglia', 'simbolo'),
    favicon: '/brands/consiglia/favicon/favicon.svg',
    appIcon: '/brands/consiglia/favicon/apple-touch-icon.png',
    colorNavegador: '#05284F',
    identidad: {
      azul: '#05284F',
      segundo: '#10819B',
      segundoClaro: '#4CC3D6',
      interfaz: '#FFE601',
    },
    textos: {
      'login.titular': 'Cierra el día con todo conciliado.',
      'login.bajada':
        'Cruzamos las ventas de tu ERP con la caja, los bancos, los datáfonos, las plataformas, los pagos digitales y la cartera para que veas al instante qué cuadra y qué falta.',
      'login.panel_cierre': 'Todo conciliado.',
      'login.panel_fuentes': 'Caja · Bancos · Datáfonos · Plataformas · Pagos digitales · Cartera',
      'login.estado_ok': 'Conciliado',
    },
  },
  match360: {
    id: 'match360',
    nombre: 'Match360',
    lema: 'Conciliación y Control al Instante',
    fuente: 'Rethink Sans Variable',
    fuenteArchivo: '/fonts/rethink-sans-latin-wght-normal.woff2',
    horizontal: variantes('match360', 'horizontal'),
    horizontalLema: variantes('match360', 'horizontal-lema'),
    vertical: variantes('match360', 'vertical'),
    simbolo: variantes('match360', 'simbolo'),
    favicon: '/brands/match360/favicon/favicon.svg',
    appIcon: '/brands/match360/favicon/apple-touch-icon.png',
    colorNavegador: '#06296A',
    identidad: {
      azul: '#06296A',
      segundo: '#02A7BD',
      segundoClaro: '#3FD0E3',
      interfaz: '#06296A',
    },
    textos: {
      'login.titular': 'Cada venta, con su respaldo.',
      'login.bajada':
        'Caja, bancos, datáfonos, plataformas, pagos digitales y cartera, conciliados contra las ventas de tu ERP, punto por punto.',
      'login.panel_cierre': 'Todo cuadra.',
      'login.panel_fuentes': 'Caja · Bancos · Datáfonos · Plataformas · Pagos digitales · Cartera',
      'login.estado_ok': 'Cuadrado',
    },
  },
};

/** Normaliza para comparar: minúsculas, sin tildes ni separadores. */
function plano(valor: string | undefined | null): string {
  return (valor ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/** `true` si el valor nombra una marca que el front sabe pintar. */
export function esMarca(valor: unknown): valor is MarcaId {
  return typeof valor === 'string' && (MARCAS as string[]).includes(valor);
}

/**
 * Marca de una edición. Manda `branding.marca` cuando viene; si no (backend
 * anterior al campo, o la edición nunca la eligió), se deduce del nombre de
 * producto y, después, del código: una edición llamada «Match 360» se viste de
 * Match360 aunque su código siga siendo `consiglia`, porque el código es el
 * nombre técnico interno y el nombre es lo que el cliente compró. Lo que no se
 * reconoce cae a Consiglia, la marca con la que nacieron todas las instalaciones.
 */
export function resolverMarca(
  marca: string | undefined | null,
  codigo?: string | null,
  nombreProducto?: string | null,
): MarcaId {
  const explicita = plano(marca);
  if (esMarca(explicita)) return explicita;

  for (const pista of [plano(nombreProducto), plano(codigo)]) {
    if (pista.includes('match')) return 'match360';
    if (pista.includes('consiglia')) return 'consiglia';
  }
  return 'consiglia';
}

/**
 * `true` si `valor` es el valor por defecto de OTRA marca. Las ediciones viejas
 * tienen sembrados la fuente, los colores y el título de Consiglia; cuando esa
 * edición pasa a venderse como Match360 esos valores no son una personalización,
 * son el paquete anterior, y deben ceder al de la marca elegida.
 */
export function esHerenciaDeOtraMarca(
  marca: MarcaId,
  valor: string,
  obtener: (paquete: PaqueteMarca) => string | undefined,
): boolean {
  const limpio = plano(valor);
  if (!limpio) return false;
  return MARCAS.some(
    (otra) => otra !== marca && plano(obtener(PAQUETES_MARCA[otra])) === limpio,
  );
}

/**
 * Colores con los que cada marca nació sembrada en la base. Sólo sirven para
 * reconocerlos como herencia (ver `esHerenciaDeOtraMarca`); la paleta real está
 * en `src/styles.css`.
 */
export const COLORES_SEMBRADOS: Readonly<
  Record<MarcaId, Readonly<Record<string, string>>>
> = {
  consiglia: { primary: '#ffe601', secondary: '#ff5f00' },
  match360: {},
};
